import React from 'react';
import { SlideWrapper, GlassCard, SectionLabel } from './SharedComponents';

export const ConvergenceIntroSlide = ({ slide }) => (
  <SlideWrapper slide={slide} background={
    <div className="fixed inset-0 z-0">
      <div className="absolute inset-0 bg-gradient-to-b from-[#030308] via-[#060612] to-[#030308]" />
      <div className="absolute top-1/4 left-1/3 w-[600px] h-[600px] bg-[#8b5cf6] opacity-[0.06] blur-[180px] rounded-full" />
      <div className="absolute top-1/4 right-1/3 w-[600px] h-[600px] bg-[#0068ff] opacity-[0.06] blur-[180px] rounded-full" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#0068ff] opacity-[0.05] blur-[150px] rounded-full" />
      <svg className="absolute inset-0 w-full h-full opacity-[0.02]" viewBox="0 0 1920 1080">
        <line x1="640" y1="200" x2="960" y2="550" stroke="#8b5cf6" strokeWidth="1" strokeDasharray="6 6" />
        <line x1="1280" y1="200" x2="960" y2="550" stroke="#0068ff" strokeWidth="1" strokeDasharray="6 6" />
        <circle cx="960" cy="550" r="4" fill="#0068ff" opacity="0.5" />
      </svg>
      <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
        backgroundSize: '50px 50px'
      }} />
    </div>
  }>
    <div className="relative z-10 flex flex-col items-center justify-center h-full px-8 py-6">
      <SectionLabel delay={0}>The Foundation</SectionLabel>
      <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mt-4 mb-2 animate-slide-up">{slide.title}</h2>
      <p className="text-white/35 text-base mb-8 animate-fade-in" style={{ animationDelay: '0.1s' }}>{slide.subtitle}</p>

      <div className="flex items-stretch gap-6 w-full max-w-5xl mb-6">
        {slide.pillars.map((pillar, idx) => {
          const color = idx === 0 ? '#8b5cf6' : '#0068ff';
          const icon = idx === 0 ? (
            <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke={color} strokeWidth="1.5"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><path d="M10 6.5h4M6.5 10v4M17.5 10v4M10 17.5h4" /></svg>
          ) : (
            <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke={color} strokeWidth="1.5"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 4-6 8-6s8 2 8 6" /><circle cx="12" cy="8" r="1" fill={color} /></svg>
          );
          return (
            <GlassCard key={idx} delay={0.15 + idx * 0.1} className="flex-1 p-6" style={{
              background: `linear-gradient(135deg, ${color}10, ${color}05)`,
              borderColor: `${color}30`
            }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>{icon}</div>
                <div>
                  <span className="text-xl font-bold" style={{ color }}>{pillar.name}</span>
                  <span className="text-white/50 text-sm ml-2">{pillar.role}</span>
                </div>
              </div>
              <p className="text-white/40 text-sm leading-relaxed mb-4">{pillar.description}</p>
              <div className="pt-3 border-t border-white/[0.06]">
                <span className="text-white font-semibold text-sm">{pillar.insight}</span>
              </div>
            </GlassCard>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mb-6 animate-scale-in" style={{ animationDelay: '0.35s' }}>
        <div className="h-[1px] w-12 bg-gradient-to-r from-transparent to-[#8b5cf6]/40" />
        <GlassCard className="px-8 py-4 text-center" style={{
          borderColor: '#0068ff40',
          boxShadow: '0 0 40px rgba(0,104,255,0.12)',
          background: 'linear-gradient(135deg, rgba(0,104,255,0.08), rgba(139,92,246,0.08))'
        }}>
          <div className="text-white/40 text-xs uppercase tracking-widest mb-1">Together they enable</div>
          <span className="text-xl font-bold bg-gradient-to-r from-[#8b5cf6] via-[#3d8bff] to-[#0068ff] bg-clip-text text-transparent">{slide.result}</span>
        </GlassCard>
        <div className="h-[1px] w-12 bg-gradient-to-l from-transparent to-[#0068ff]/40" />
      </div>

      <div className="flex items-center gap-3 mb-7 flex-wrap justify-center animate-fade-in" style={{ animationDelay: '0.45s' }}>
        {slide.measures.map((m, idx) => {
          const colors = ['#0068ff', '#22c55e', '#f43f5e', '#f59e0b', '#06b6d4'];
          return (
            <span key={idx} className="px-3 py-1 rounded-full text-xs font-medium" style={{
              color: colors[idx], background: `${colors[idx]}10`, border: `1px solid ${colors[idx]}20`
            }}>{m}</span>
          );
        })}
      </div>

      <GlassCard delay={0.55} className="w-full max-w-3xl px-6 py-4">
        <div className="flex items-center justify-between">
          {slide.evolution.map((step, idx) => {
            const isLast = idx === slide.evolution.length - 1;
            return (
              <React.Fragment key={idx}>
                <div className="text-center flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-white/25 mb-1">{step.era}</div>
                  <div className={`text-sm ${isLast ? 'font-bold' : 'font-medium text-white/60'}`}>
                    {isLast ? (
                      <><span className="text-white/60">{step.verb} </span><span className="text-[#0068ff]">{step.object}</span></>
                    ) : (
                      <>{step.verb} <span className="text-white/80">{step.object}</span></>
                    )}
                  </div>
                </div>
                {idx < slide.evolution.length - 1 && (
                  <div className="mx-2">
                    <svg viewBox="0 0 24 12" className="w-6 h-3 text-white/15"><path d="M 2 6 L 20 6" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M 16 2 L 22 6 L 16 10" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </GlassCard>
    </div>
  </SlideWrapper>
);
